import {useMemo} from 'react'
import React from 'react'
import {useTheme} from '@emotion/react'
import {v4 as uuid} from 'uuid'

type NestedRecord = {[k: string]: string}
//https://saurabhnativeblog.medium.com/how-to-deep-flatten-object-in-javascript-16bc5a22382
function deepFlattenToObject(obj: NestedRecord, prefix = '') {
  return Object.keys(obj).reduce<Record<string, string>>((acc, k) => {
    const pre = prefix.length ? prefix + '-' : '--'
    if (typeof obj[k] === 'object' && obj[k] !== null) {
      // eslint-disable-next-line fp/no-mutating-assign
      Object.assign(acc, deepFlattenToObject(obj[k], pre + k))
    } else {
      // eslint-disable-next-line fp/no-mutation
      acc[pre + k] = obj[k]
    }
    return acc
  }, {})
}

/**
 * This component will provide a CSS variable theme, optionally scoped down to the children.
 */
export const CSSThemeProvider: React.FC<{
  children: React.ReactNode
  isGlobal?: boolean
}> = ({children, isGlobal = true}) => {
  const theme = useTheme()
  const uniqueId = useMemo(() => `cssthemeprovider-${uuid().slice(0, 6)}`, [])

  const serializedTheme = useMemo(
    () =>
      Object.entries(deepFlattenToObject(theme as unknown as NestedRecord))
        .map(([key, value]) => [key.replace('--colors', '-'), value])
        .map(([key, value]) => {
          if (
            key.startsWith('--layout-spacing-') ||
            key.startsWith('--layout-radius-') ||
            key.startsWith('--layout-borderWidth-')
          ) {
            return `${key}: ${value}px`
          }
          if (key.startsWith('--shadows-')) {
            return `${key.replace('shadows', 'shadow')}: ${value}`
          }
          return `${key}: ${value}`
        })
        .join(';'),
    [theme]
  )

  // Sometimes we want to override the global theme (i.e. multi-brand snapshots).
  // For these purposes we generate a unique id to wrap the children
  const selector = isGlobal ? ':root' : `#${uniqueId} `

  return (
    <>
      <style type="text/css">{`${selector} {${serializedTheme}}`}</style>
      {isGlobal ? <>{children}</> : <div id={uniqueId}>{children}</div>}
    </>
  )
}
